import { useEffect, useMemo, useState } from 'react';
import { Home, MessageSquare } from 'lucide-react';
import { apiService, ContainerApplyEntry, ContainerApplyResult, EssentialsData } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { useSnapshotDraft, getDraftPayload, draftKey } from '@/stores/unsavedDrafts.store';
import { MinecraftText } from '@/components/config/MinecraftText';
import {
  ApplyReport, ContainerScope, ScopeEntry, applyResultOf, useContainersData, useProjectContainers,
} from '@/components/config/AllContainers';
import styles from '@/pages/GradesPage.module.scss';

interface EssValues { homeLimit: string; chatFormat: string; premiumChatFormat: string }

/** Brouillon d'un grade : les valeurs, et `retry` quand un container n'a pas reçu le dernier enregistrement. */
interface EssDraft extends EssValues { retry?: boolean }

const FIELD_LABELS: Record<keyof EssValues, string> = {
  homeLimit: 'nombre de homes',
  chatFormat: 'format du chat',
  premiumChatFormat: 'format du chat premium',
};

const NOT_FOUND: EssentialsData = { found: false, file: null, groups: [] };

/** Valeurs d'un grade dans la config essentials d'un container, au format du formulaire. */
function valuesOf(data: EssentialsData | undefined, grade: string): EssValues {
  const g = data?.groups.find((x) => x.name === grade);
  return {
    homeLimit: g?.homeLimit != null ? String(g.homeLimit) : '',
    chatFormat: g?.chatFormat ?? '',
    premiumChatFormat: g?.premiumChatFormat ?? '',
  };
}

function differingFields(a: EssValues, b: EssValues): string[] {
  return (Object.keys(FIELD_LABELS) as Array<keyof EssValues>).filter((k) => a[k] !== b[k]);
}

/**
 * Rendu du chat d'un grade (Supervision > Grades, onglet Cosmétiques) : format de chat avec aperçu
 * Minecraft en direct, et nombre de homes (même section essentials). Commun à tous les
 * containers du projet : lu dans le premier container (par id) qui porte la config essentials,
 * enregistré (barre du bas) dans chaque container qui la porte.
 */
export function GradeEssentials({ grade, readOnly = false }: { grade: string; readOnly?: boolean }) {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const { containers, loaded } = useProjectContainers();
  const [reload, setReload] = useState(0);
  const { data: all } = useContainersData(
    containers,
    (id) => apiService.getContainerEssentials(id).then((r) => r.data ?? NOT_FOUND),
    reload,
  );
  const [homeLimit, setHomeLimit] = useState('');
  const [chatFormat, setChatFormat] = useState('');
  const [premiumChatFormat, setPremiumChatFormat] = useState('');
  const [orig, setOrig] = useState('');
  /**
   * Dernier enregistrement en échec sur au moins un container. La référence, elle, a pu être écrite :
   * relue, elle égale le formulaire, et sans ce drapeau le brouillon quitterait la barre alors qu'un
   * container porte encore les anciennes valeurs.
   */
  const [retry, setRetry] = useState(false);
  const [report, setReport] = useState<ContainerApplyResult | null>(null);

  /** Référence : le premier container (ordre stable) qui porte la config essentials. */
  const ref = loaded && all ? containers.find((c) => all[c.id]?.found) : undefined;
  const refValues = useMemo(() => (ref && all ? valuesOf(all[ref.id], grade) : null), [all, ref, grade]);
  const key = draftKey('grade-essentials', activeGameId, grade);

  // Valeurs de la référence, ou le brouillon en attente (retour d'onglet ou de grade).
  useEffect(() => {
    if (!refValues) return;
    setOrig(JSON.stringify(refValues));
    const pending = getDraftPayload(key) as EssDraft | undefined;
    setRetry(!!pending?.retry);
    setHomeLimit(pending?.homeLimit ?? refValues.homeLimit);
    setChatFormat(pending?.chatFormat ?? refValues.chatFormat);
    setPremiumChatFormat(pending?.premiumChatFormat ?? refValues.premiumChatFormat);
  }, [refValues, key]);

  const ctx = { hostname: ref?.type ?? ref?.id ?? 'server', player: 'Steve', faction: 'MaFaction', message: 'Salut !' };
  const form: EssValues = { homeLimit, chatFormat, premiumChatFormat };
  const dirty = !readOnly && !!ref && (retry || JSON.stringify(form) !== orig);

  const scope: ScopeEntry[] = containers.map((c) => {
    const d = all?.[c.id];
    if (!d?.found || !refValues) return { containerId: c.id, containerName: c.name, status: 'SKIPPED_FILE_MISSING' };
    if (c.id === ref?.id) return { containerId: c.id, containerName: c.name, status: 'REFERENCE' };
    const differs = differingFields(valuesOf(d, grade), refValues);
    return { containerId: c.id, containerName: c.name, status: differs.length ? 'DIFFERENT' : 'SAME', differs };
  });

  /** Écrit le grade dans chaque container du projet qui porte la config essentials. */
  const save = async () => {
    const entries: ContainerApplyEntry[] = [];
    for (const c of containers) {
      const d = all?.[c.id];
      const base = { containerId: c.id, containerName: c.name, path: d?.file ?? null };
      if (!d?.found) {
        entries.push({ ...base, status: 'SKIPPED_FILE_MISSING', message: 'Config essentials introuvable (container non synchronisé ?).' });
        continue;
      }
      if (differingFields(valuesOf(d, grade), form).length === 0) {
        entries.push({ ...base, status: 'UNCHANGED', message: 'Déjà identique.' });
        continue;
      }
      // Nombre de homes vidé : retiré, pour que ce container s'aligne aussi sur un grade sans limite.
      const blank = homeLimit.trim() === '';
      const res = await apiService.saveContainerEssentialsGroup(
        c.id, grade, blank ? null : Number(homeLimit), chatFormat, premiumChatFormat, { clearHomeLimit: blank },
      );
      entries.push(res.data?.success
        ? { ...base, status: 'WRITTEN', message: `Grade ${grade}` }
        : { ...base, status: 'FAILED', message: res.data?.message ?? res.error ?? 'Enregistrement en échec' });
    }
    const result = applyResultOf(entries);
    setReport(result);
    if (result.success) setOrig(JSON.stringify(form));
    setRetry(!result.success);
    setReload((k) => k + 1);
    return result.success;
  };

  useSnapshotDraft({
    kind: 'grade-essentials',
    groupId: activeGameId,
    identifier: grade,
    label: `Chat · ${grade} · tous les containers`,
    dirty,
    payload: { ...form, retry },
    save,
  });

  if (loaded && containers.length === 0) return null;

  return (
    <div className={styles.essBlock}>
      <div className={styles.sectionLabel}><MessageSquare size={14} /> Rendu du chat &amp; homes</div>

      {!loaded || !all ? (
        <div className={styles.loadingState}><div className={styles.spinner} /><span>Chargement…</span></div>
      ) : !ref ? (
        <p className={styles.muted}>
          Config essentials introuvable dans les containers du projet : synchronise-les dans Paramètres généraux → Containers.
        </p>
      ) : (
        <>
          <ContainerScope entries={scope} referencePath={all[ref.id]?.file}
            labelOf={(k) => FIELD_LABELS[k as keyof EssValues] ?? k} />
          <div className={styles.essField}>
            <label className={styles.essLabel}><Home size={13} /> Nombre de homes</label>
            <input className={styles.input} type="number" min={0} value={homeLimit} disabled={readOnly} onChange={(e) => setHomeLimit(e.target.value)} placeholder="ex. 18" />
          </div>

          <div className={styles.essField}>
            <label className={styles.essLabel}>Format du chat</label>
            <input className={styles.input} value={chatFormat} disabled={readOnly} onChange={(e) => setChatFormat(e.target.value)} placeholder="§7[{hostname}]…" spellCheck={false} />
            <div className={styles.essPreview}><MinecraftText text={chatFormat} context={ctx} /></div>
          </div>

          <div className={styles.essField}>
            <label className={styles.essLabel}>Format du chat premium (optionnel)</label>
            <input className={styles.input} value={premiumChatFormat} disabled={readOnly} onChange={(e) => setPremiumChatFormat(e.target.value)} placeholder="(vide = aucun)" spellCheck={false} />
            {premiumChatFormat.trim() !== '' && <div className={styles.essPreview}><MinecraftText text={premiumChatFormat} context={ctx} /></div>}
          </div>

          {report && <ApplyReport result={report} onClose={() => setReport(null)} />}

          <p className={styles.muted}>
            {retry
              ? "Un container n'a pas reçu le dernier enregistrement : enregistre à nouveau via la barre du bas."
              : dirty
                ? 'Modifications en attente : enregistre-les via la barre du bas, elles seront écrites dans tous les containers.'
                : 'Enregistré.'} Envoie ensuite les containers avec le bouton Envoyer.
          </p>
        </>
      )}
    </div>
  );
}
